/**
 * useWakeLock — the SINGLE shared screen-wake-lock primitive.
 *
 * Operator brief (May 14, 2026 — Active Operation P0 sprint):
 *   "One shared wake-lock hook reused by both upload screens and
 *    Work Mode. If wake-lock is unavailable or fails, show a plain
 *    fallback warning."
 *
 * Wraps `navigator.wakeLock.request('screen')` with:
 *   • Idempotent acquire — calling `useWakeLock(true)` repeatedly
 *     keeps a SINGLE lock alive; flipping `active` toggles it.
 *   • Auto re-acquire on visibilitychange (iOS Safari + Android
 *     drop the lock when the tab is backgrounded).
 *   • Auto-release on unmount.
 *   • Returns `{ supported, acquired, error, fallbackWarning }`
 *     so consumers can show a clear fallback message when the
 *     browser lacks Wake Lock API support (Safari < 16.4,
 *     Firefox, in-app browsers).
 *
 * Reused by:
 *   • ClipBasketModal (during uploading_clips)
 *   • TrainingMode (during pupil-mounted-phone recording)
 *   • Work Mode (during arrived/in_progress/completed lesson)
 *
 * NOTE: Wake Lock requires HTTPS + a visible tab. We never request
 * it speculatively — only when the consumer says `active = true`.
 */
import { useEffect, useRef, useState, useCallback } from "react";

const SUPPORTED = typeof navigator !== "undefined" && "wakeLock" in navigator;

export default function useWakeLock(active, opts = {}) {
  const { label = "active-op" } = opts;
  const [acquired, setAcquired] = useState(false);
  const [error, setError] = useState(null);
  const sentinelRef = useRef(null);

  const release = useCallback(async () => {
    const s = sentinelRef.current;
    sentinelRef.current = null;
    if (s) {
      try { await s.release(); } catch { /* noop */ }
    }
    setAcquired(false);
  }, []);

  const acquire = useCallback(async () => {
    if (!SUPPORTED) {
      setError("wake_lock_unsupported");
      return;
    }
    if (sentinelRef.current) return; // already held
    try {
      const sentinel = await navigator.wakeLock.request("screen");
      sentinelRef.current = sentinel;
      setAcquired(true);
      setError(null);
      // The browser will release the lock automatically when the tab
      // is hidden; we re-acquire on visibilitychange (below).
      sentinel.addEventListener("release", () => {
        if (sentinelRef.current === sentinel) {
          sentinelRef.current = null;
          setAcquired(false);
          // eslint-disable-next-line no-console
          console.debug(`[wake-lock:${label}] released_by_browser`);
        }
      });
      // eslint-disable-next-line no-console
      console.debug(`[wake-lock:${label}] acquired`);
    } catch (e) {
      setError(e?.name || "wake_lock_failed");
      // eslint-disable-next-line no-console
      console.debug(`[wake-lock:${label}] acquire_failed`, e?.name);
    }
  }, [label]);

  useEffect(() => {
    if (active) {
      acquire();
    } else {
      release();
    }
    return () => { release(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  // Re-acquire when the tab returns to the foreground.
  useEffect(() => {
    if (!active) return undefined;
    const onVisible = () => {
      if (document.visibilityState === "visible" && !sentinelRef.current) {
        acquire();
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [active, acquire]);

  // Build a plain-text fallback warning for consumers to surface
  // when the browser can't keep the screen awake.
  const fallbackWarning = !SUPPORTED
    ? "Your browser can't keep the screen awake automatically. " +
      "Tap the screen occasionally so your phone doesn't lock."
    : error && !acquired
    ? "Couldn't keep the screen awake. Tap occasionally so your phone doesn't lock."
    : null;

  return {
    supported: SUPPORTED,
    acquired,
    error,
    fallbackWarning,
  };
}
